/**
 * The published page's own tab icon - `SAAS.md` §3.181, FB-954.
 *
 * **A missed port on both halves.** Legacy's marketing editor has a `MediaUploader` for `faviconUrl` and
 * legacy's `App.tsx` ends its settings block by creating or updating `link[rel='icon']` from it. AGOS had
 * neither, so §3.180's static file - shipped an hour before this - was the only answer the shopfront had.
 *
 * **Its own module rather than a block in `App.tsx`, and rather than a function in `site.ts`.** `site.ts` is
 * deliberately DOM-free: `cssVariables` and `siteAttributes` return records and the caller applies them, which
 * is what lets them be driven without a browser. This one's whole behaviour *is* the DOM - create the element
 * if the head has none, put the previous value back on the way out - so it cannot be expressed as a record, and
 * a block inside an effect could only be tested by rendering `App` behind a `QueryClientProvider` with the
 * generated hook mocked. Here it is driven directly in jsdom.
 */

/**
 * Point the tab icon at `href`, and give back the undo.
 *
 * `null` means *this page says nothing*, which is the ordinary case: the owner's published page carries empty
 * `settings`. Then nothing is touched at all and `index.html`'s own static icon stands - which is the right
 * answer rather than a fallback, because §3.180 ships that file deliberately.
 *
 * **The element is created when the head has none**, exactly as legacy's does and as `socialMeta`'s loop
 * already does for a trimmed head. In production `index.html` ships the tag, so this only ever rewrites.
 *
 * **The undo restores the previous href rather than removing the tag**, and that distinction is the whole of
 * FB-953: the static icon is the app's own answer, not a leftover, so dropping it would leave a visitor with
 * the browser's generic glyph. A tag this function created is removed, because then there was no answer
 * before it.
 */
export function applyFavicon(head: HTMLHeadElement, href: string | null): () => void {
  if (href === null) return () => {}

  const existing = head.querySelector<HTMLLinkElement>("link[rel='icon']")
  const previous = existing?.getAttribute('href') ?? null
  const icon = existing ?? head.ownerDocument.createElement('link')
  if (existing === null) {
    icon.rel = 'icon'
    head.appendChild(icon)
  }
  icon.setAttribute('href', href)

  return () => {
    if (existing === null) icon.remove()
    else if (previous === null) icon.removeAttribute('href')
    else icon.setAttribute('href', previous)
  }
}
